import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Trash2, Pencil, ChefHat, Store } from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "../../components/PasswordInput";

const EMPTY = { name: "", username: "", password: "", kitchen_id: "" };

export default function AdminChefs() {
  const [chefs, setChefs] = useState([]);
  const [kitchens, setKitchens] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/admin/chefs").then((r) => setChefs(r.data));
  useEffect(() => { load(); api.get("/admin/kitchens").then((r) => setKitchens(r.data)); }, []);

  const save = async () => {
    try {
      const payload = { ...form, kitchen_id: form.kitchen_id || null };
      if (editId) await api.put(`/admin/chefs/${editId}`, payload);
      else await api.post("/admin/chefs", payload);
      toast.success("Kitchen chef saved"); setOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/chefs/${id}`); toast.success("Removed"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const edit = (c) => { setForm({ name: c.name, username: c.username, password: "", kitchen_id: c.kitchen_id || "" }); setEditId(c.id); setOpen(true); };

  return (
    <div data-testid="admin-chefs">
      <div className="flex justify-between items-center mb-6">
        <div><h1 className="font-serif text-3xl text-navy">Kitchen Chefs</h1><p className="text-sm text-muted-foreground">Create separate logins for kitchen staff, linked to an operating kitchen</p></div>
        <button onClick={() => { setForm(EMPTY); setEditId(null); setOpen(true); }} data-testid="new-chef-btn" className="flex items-center gap-2 bg-navy text-cream text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-navy-soft"><Plus size={15} /> Add Chef</button>
      </div>

      <div className="bg-white rounded-xl border border-black/5 overflow-x-auto">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="bg-beige text-navy text-left"><tr><th className="p-3">Name</th><th className="p-3">Login ID</th><th className="p-3">Kitchen</th><th className="p-3">Login URL</th><th className="p-3"></th></tr></thead>
          <tbody>
            {chefs.length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No kitchen chefs yet</td></tr> :
              chefs.map((c) => (
                <tr key={c.id} className="border-t border-black/5 hover:bg-beige/40" data-testid={`chef-row-${c.username}`}>
                  <td className="p-3 font-medium text-navy flex items-center gap-2"><ChefHat size={15} className="text-gold" /> {c.name}</td>
                  <td className="p-3 text-muted-foreground">{c.username}</td>
                  <td className="p-3"><span className="text-xs flex items-center gap-1 text-navy"><Store size={12} className="text-gold" /> {c.kitchen_name || "Unassigned"}</span></td>
                  <td className="p-3 text-xs text-muted-foreground">/kitchen/login</td>
                  <td className="p-3"><div className="flex gap-2"><button onClick={() => edit(c)} className="text-muted-foreground hover:text-navy" data-testid={`edit-chef-${c.username}`}><Pencil size={15} /></button><button onClick={() => del(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`delete-chef-${c.username}`}><Trash2 size={15} /></button></div></td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-serif text-2xl">{editId ? "Edit" : "New"} Kitchen Chef</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input placeholder="Chef Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="chef-name" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" />
            <input placeholder="Login ID (username)" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} data-testid="chef-username" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" />
            <PasswordInput placeholder={editId ? "New password (leave blank to keep)" : "Password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} testId="chef-password" className="w-full border border-black/15 rounded-lg px-3 py-2 text-sm" />
            <Select value={form.kitchen_id} onValueChange={(v) => setForm({ ...form, kitchen_id: v })}>
              <SelectTrigger data-testid="chef-kitchen"><SelectValue placeholder="Assign to kitchen" /></SelectTrigger>
              <SelectContent>{kitchens.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}</SelectContent>
            </Select>
            <button onClick={save} data-testid="save-chef-btn" className="w-full bg-navy text-cream font-semibold py-3 rounded-full hover:bg-navy-soft">Save Chef</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
